import React, {useEffect, useState} from 'react';
import {WbIcon} from '@workbench/react';

import {useTranslation} from 'react-i18next';
import {IProduct} from '../../../interfaces/IProduct';
import {IColumn, IProductSheet,} from '../../../interfaces/IProductSheet';
import {TABLE_HEADER} from '../../../globals/Constants';
import './SheetTable.css';


export const SheetTable = (props: {
    sheet: IProductSheet;
    language: string;
    showMore: boolean;
    setShowMore: (showMore: boolean) => void;
    setLinkedFluidCount: (count: number) => void;
    setUnlinkedFluidCount: (count: number) => void;
}) => {
    const {t} = useTranslation();
    const [, setSheet] = useState(props.sheet);

    let products = props.sheet.productCollection;

    let rowsWithLinks = products?.filter(row => row?.link !== null) || [];
    let rowsWithoutLinks = products?.filter(row => row?.link === null) || [];
    props.setLinkedFluidCount(rowsWithLinks?.length || 0);
    props.setUnlinkedFluidCount(rowsWithoutLinks?.length || 0);

    useEffect(() => {
        setSheet(props.sheet);

        // Only scroll and reset the showMore state if the ACTUAL sheet document changed
        // (e.g., the user loaded a completely different sheet number from the API)
        const tableElement = document?.getElementById('sheetTableId') as Element;
        tableElement?.scrollTo({
            top: 0,
            behavior: 'smooth',
        });

        // Keep the initial toggle calculation restricted to when a brand new page loads
        props.setShowMore(rowsWithLinks.length === 0 && rowsWithoutLinks.length > 0);

    }, [props.sheet.sheetNumber]); // 🟢 Changed from props.sheet to props.sheet.sheetNumber

    // Separate effect to handle counts when the items inside the table change
    useEffect(() => {
        props.setLinkedFluidCount(rowsWithLinks?.length || 0);
        props.setUnlinkedFluidCount(rowsWithoutLinks?.length || 0);
    }, [rowsWithLinks?.length, rowsWithoutLinks?.length, props.language]);


    const columns: IColumn[] = [
        {key: 'productName', check: (row: IProduct) => row?.productName !== null, isLink: true},
        {key: 'type30', check: (row: IProduct) => row?.type30 !== false},
        {key: 'type40', check: (row: IProduct) => row?.type40 !== false},
        {key: 'type80', check: (row: IProduct) => row?.type80 !== false},
        {key: 'type90', check: (row: IProduct) => row?.type90 !== false},
        {key: 'type0W', check: (row: IProduct) => row?.type0W !== false},
        {key: 'type0W20', check: (row: IProduct) => row?.type0W20 !== false},
        {key: 'type0W30', check: (row: IProduct) => row?.type0W30 !== false},
        {key: 'type0W40', check: (row: IProduct) => row?.type0W40 !== false},
        {key: 'type5W20', check: (row: IProduct) => row?.type5W20 !== false},
        {key: 'type5W30', check: (row: IProduct) => row?.type5W30 !== false},
        {key: 'type5W40', check: (row: IProduct) => row?.type5W40 !== false},
        {key: 'type5W50', check: (row: IProduct) => row?.type5W50 !== false},
        {key: 'type10W30', check: (row: IProduct) => row?.type10W30 !== false},
        {key: 'type10W40', check: (row: IProduct) => row?.type10W40 !== false},
        {key: 'type10W60', check: (row: IProduct) => row?.type10W60 !== false},
        {key: 'type15W40', check: (row: IProduct) => row?.type15W40 !== false},
        {key: 'type20W50', check: (row: IProduct) => row?.type20W50 !== false},
        {key: 'type75W', check: (row: IProduct) => row?.type75W !== false},
        {key: 'type75W80', check: (row: IProduct) => row?.type75W80 !== false},
        {key: 'type75W85', check: (row: IProduct) => row?.type75W85 !== false},
        {key: 'type75W90', check: (row: IProduct) => row?.type75W90 !== false},
        {key: 'type80W', check: (row: IProduct) => row?.type80W !== false},
        {key: 'type80W90', check: (row: IProduct) => row?.type80W90 !== false},
        {key: 'type80W85', check: (row: IProduct) => row?.type80W85 !== false},
        {key: 'type85W90', check: (row: IProduct) => row?.type85W90 !== false},
        {key: 'description', check: (row: IProduct) => row?.description !== null},
    ];


    return (
        <div>
            {products?.length > 0 && (
                <div id='sheetTableId' className='wb-padding-s sheetTableDesktop'>
                    <table className='wb-table wb-table--striped sheetTable'>
                        <thead className='tableHeader'>
                        <tr>
                            {columns.map(column =>
                                    products?.some(column.check) && (
                                        <th scope='col' key={column.key} className={`${column.key}`}>
                                            {t(TABLE_HEADER[column.key].value)}
                                        </th>
                                    )
                            )}
                        </tr>
                        </thead>
                        <tbody>
                        {rowsWithLinks.map((row, key) => (
                            <tr key={key}>
                                {columns.map(column =>
                                        products?.some(column.check) && (
                                            <td key={column.key}>
                                                {column.isLink && row?.link ? (
                                                    <a
                                                        target='_blank'
                                                        className='wb-link dt-link'
                                                        href={row?.link} rel="noreferrer"
                                                    >
                                                        {row[column.key]}
                                                    </a>
                                                ) : column.key.startsWith('type') && row[column.key] ? (
                                                    <WbIcon name='checkmark' className='iconCheck'></WbIcon>
                                                ) : (
                                                    row[column.key]
                                                )}
                                            </td>
                                        )
                                )}
                            </tr>
                        ))}
                        {props.showMore && rowsWithoutLinks.map((row, key) => (
                            <tr key={key}>
                                {columns.map(column =>
                                        products?.some(column.check) && (
                                            <td key={column.key}>
                                                {column.isLink && row?.link ? (
                                                    <a
                                                        target='_blank'
                                                        className='wb-link'
                                                        href={row?.link} rel="noreferrer"
                                                    >
                                                        {row[column.key]}
                                                    </a>
                                                ) : column.key.startsWith('type') && row[column.key] ? (
                                                    <WbIcon name='checkmark' className='iconCheck'></WbIcon>
                                                ) : (
                                                    row[column.key]
                                                )}
                                            </td>
                                        )
                                )}
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};
